import { useState, useEffect, useCallback, useRef } from 'react';

interface UseSessionTimeoutOptions {
  timeoutMinutes: number;       // Time before showing warning modal (in minutes)
  warningSeconds: number;       // Countdown duration in the warning modal
  isPaused: boolean;            // Pause timeout (e.g., during processing)
  onTimeout: () => void;        // Called when session times out
}

interface UseSessionTimeoutReturn {
  showWarning: boolean;
  resetTimeout: () => void;
  handleStayActive: () => void;
  handleWarningTimeout: () => void;
}

export function useSessionTimeout({
  timeoutMinutes,
  warningSeconds,
  isPaused,
  onTimeout,
}: UseSessionTimeoutOptions): UseSessionTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const timeoutMs = timeoutMinutes * 60 * 1000;

  // Clear existing timeout
  const clearTimeoutTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Start/restart the timeout timer
  const startTimeout = useCallback(() => {
    clearTimeoutTimer();

    if (isPaused) return;

    timeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, timeoutMs);
  }, [clearTimeoutTimer, isPaused, timeoutMs]);

  // Reset timeout on user activity
  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (!showWarning) {
      startTimeout();
    }
  }, [showWarning, startTimeout]);

  // Handle user clicking "I'm still here"
  const handleStayActive = useCallback(() => {
    setShowWarning(false);
    lastActivityRef.current = Date.now();
    startTimeout();
  }, [startTimeout]);

  // Handle timeout (user didn't respond to warning)
  const handleTimeout = useCallback(() => {
    setShowWarning(false);
    clearTimeoutTimer();
    onTimeout();
  }, [clearTimeoutTimer, onTimeout]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];

    const handleActivity = () => {
      if (!showWarning && !isPaused) {
        resetTimeout();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimeout, showWarning, isPaused]);

  // Start timeout on mount and when pause state changes
  useEffect(() => {
    if (isPaused) {
      clearTimeoutTimer();
      setShowWarning(false);
    } else {
      startTimeout();
    }

    return () => {
      clearTimeoutTimer();
    };
  }, [isPaused, startTimeout, clearTimeoutTimer]);

  return {
    showWarning,
    resetTimeout,
    handleStayActive,
    handleWarningTimeout: handleTimeout,
  };
}

export default useSessionTimeout;
